import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton,
  Card, CardContent, CardActionArea, Divider, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Collapse,
  useMediaQuery, useTheme, Alert,
} from '@mui/material';
import { ArrowBack, CheckCircle, Cancel, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import evaluationsApi, { Evaluation, EvaluationItem, ApprovalItemUpdate } from '../services/evaluationService';
import { formatApiError } from '../utils/formatApiError';
import { fmtMoney, fmtPct } from '../utils/formatMoney';

const EvaluationApprovalPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [approvalItems, setApprovalItems] = useState<Record<number, { approved_price: number | null; item_note: string }>>({});
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState('');
  const [collapsedItems, setCollapsedItems] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (id) {
      setLoading(true);
      evaluationsApi.get(Number(id))
        .then(ev => {
          setEvaluation(ev);
          const map: Record<number, { approved_price: number | null; item_note: string }> = {};
          ev.items.forEach(item => {
            if (item.id) {
              map[item.id] = {
                approved_price: item.approved_price ?? item.requested_price ?? null,
                item_note: item.item_note || '',
              };
            }
          });
          setApprovalItems(map);
        })
        .catch(e => setError(formatApiError(e, 'load')))
        .finally(() => setLoading(false));
    }
  }, [id]);

  const isSubmitted = evaluation?.status === 'SUBMITTED';

  const diffs = (item: EvaluationItem) => {
    const itemId = item.id;
    if (!itemId) return { reqDiff: null, curDiff: null };
    const approved = approvalItems[itemId]?.approved_price;
    const requested = item.requested_price;
    const current = item.current_price;
    return {
      reqDiff: approved != null && requested != null ? Number(approved) - Number(requested) : null,
      curDiff: approved != null && current != null ? Number(approved) - Number(current) : null,
    };
  };

  const toggleCollapse = (itemId: number) => {
    setCollapsedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const totals = useMemo(() => {
    if (!evaluation) return { extPrice: 0, extCost: 0, gm: null };
    let extPrice = 0;
    let extCost = 0;
    evaluation.items.forEach(item => {
      const qty = item.est_quantity ?? 0;
      const approvedPrice = item.id ? (approvalItems[item.id]?.approved_price ?? 0) : 0;
      extPrice += Number(approvedPrice) * qty;
      extCost += (Number(item.dfp_cost) || 0) * qty;
    });
    const gm = extPrice > 0 ? (extPrice - extCost) / extPrice : null;
    return { extPrice, extCost, gm };
  }, [evaluation, approvalItems]);

  const handleAction = async (action: 'approve' | 'reject') => {
    setConfirmAction(null);
    if (!evaluation) return;
    setSaving(true);
    setError('');
    try {
      const itemUpdates: ApprovalItemUpdate[] = evaluation.items
        .filter(i => i.id)
        .map(i => ({
          id: i.id!,
          approved_price: approvalItems[i.id!]?.approved_price ?? null,
          item_note: approvalItems[i.id!]?.item_note || '',
        }));

      const result = action === 'approve'
        ? await evaluationsApi.approve(evaluation.id, itemUpdates)
        : await evaluationsApi.reject(evaluation.id, itemUpdates);

      setEvaluation(result);
    } catch (e: any) {
      setError(formatApiError(e, action));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!evaluation) return <Alert severity="error">Evaluation not found</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/evaluations')} sx={{ minWidth: 48, minHeight: 48 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>
          Review: {evaluation.evaluation_number}
        </Typography>
        <Chip
          label={evaluation.status}
          size="small"
          color={
            evaluation.status === 'APPROVED' ? 'success' :
            evaluation.status === 'SUBMITTED' ? 'warning' :
            evaluation.status === 'REJECTED' ? 'error' : 'default'
          }
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Company</Typography>
            <Typography variant="body2" fontWeight={500}>{evaluation.company_name} (Tier {evaluation.company_pricing_tier})</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Contact</Typography>
            <Typography variant="body2">{evaluation.company_contact_name || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Sales Person</Typography>
            <Typography variant="body2">{evaluation.sales_person_name}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Submitted</Typography>
            <Typography variant="body2">{evaluation.submitted_at ? new Date(evaluation.submitted_at).toLocaleDateString() : '-'}</Typography>
          </Box>
        </Box>
        {evaluation.opportunity_description && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">Opportunity</Typography>
            <Typography variant="body2">{evaluation.opportunity_description}</Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Items</Typography>

        {evaluation.items.map((item, idx) => {
          const { reqDiff, curDiff } = diffs(item);
          const itemId = item.id ?? idx;
          const isCollapsed = !!collapsedItems[itemId];
          const approvedVal = item.id ? approvalItems[item.id]?.approved_price : null;
          const noteVal = item.id ? approvalItems[item.id]?.item_note : '';
          const hasApproved = approvedVal != null;

          return (
            <Card key={itemId} variant="outlined" sx={{ mb: isMobile ? 1 : 1.5 }}>
              {isMobile ? (
                <>
                  <CardActionArea onClick={() => toggleCollapse(itemId)} sx={{ px: 1, py: 0.75 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="subtitle2" noWrap sx={{ flex: 1, minWidth: 0 }}>{item.dfp_sku}</Typography>
                      <Chip label={`Qty: ${item.est_quantity ?? 0}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }} />
                      {isCollapsed ? <ExpandMore fontSize="small" sx={{ flexShrink: 0 }} /> : <ExpandLess fontSize="small" sx={{ flexShrink: 0 }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{item.description}</Typography>
                    {isCollapsed && hasApproved && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, alignItems: 'center' }}>
                        <Typography variant="caption" fontWeight={600}>Approved: {fmtMoney(approvedVal)}</Typography>
                        {reqDiff != null && (
                          <Typography variant="caption" sx={{ color: reqDiff >= 0 ? 'success.main' : 'error.main' }}>
                            ({reqDiff >= 0 ? '+' : ''}{fmtMoney(reqDiff)})
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardActionArea>
                  <Collapse in={!isCollapsed}>
                    <CardContent sx={{ px: 1, pt: 0, pb: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mt: 0.75 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">MSRP</Typography>
                          <Typography variant="body2">{fmtMoney(item.msrp)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Current</Typography>
                          <Typography variant="body2">{fmtMoney(item.current_price)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Requested</Typography>
                          <Typography variant="body2" fontWeight={600}>{fmtMoney(item.requested_price)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Competitor</Typography>
                          <Typography variant="body2">{item.competitor || '-'} {item.comp_price ? `(${fmtMoney(item.comp_price)})` : ''}</Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <TextField
                          label="Approved Price"
                          size="small"
                          type="number"
                          disabled={!isSubmitted}
                          value={approvedVal ?? ''}
                          onChange={e => {
                            if (!item.id) return;
                            setApprovalItems(prev => ({
                              ...prev,
                              [item.id!]: { ...prev[item.id!], approved_price: e.target.value ? Number(e.target.value) : null },
                            }));
                          }}
                          sx={{ width: 120 }}
                          inputProps={{ style: { textAlign: 'right' } }}
                        />
                        {reqDiff != null && (
                          <Typography variant="caption" sx={{ color: reqDiff >= 0 ? 'success.main' : 'error.main', whiteSpace: 'nowrap' }}>
                            Req: {reqDiff >= 0 ? '+' : ''}{fmtMoney(reqDiff)}
                          </Typography>
                        )}
                        {curDiff != null && (
                          <Typography variant="caption" sx={{ color: curDiff >= 0 ? 'success.main' : 'error.main', whiteSpace: 'nowrap' }}>
                            Cur: {curDiff >= 0 ? '+' : ''}{fmtMoney(curDiff)}
                          </Typography>
                        )}
                      </Box>
                      <TextField
                        label="Note"
                        size="small"
                        fullWidth
                        multiline
                        disabled={!isSubmitted}
                        value={noteVal ?? ''}
                        onChange={e => {
                          if (!item.id) return;
                          setApprovalItems(prev => ({
                            ...prev,
                            [item.id!]: { ...prev[item.id!], item_note: e.target.value },
                          }));
                        }}
                        sx={{ mt: 0.75 }}
                      />

                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">DFP Cost: {fmtMoney(item.dfp_cost)}</Typography>
                        <Typography variant="caption" color="text.secondary">GM: {fmtPct(item.dfp_unit_gm)}</Typography>
                        <Typography variant="caption" color="text.secondary">Ext: {fmtMoney(item.ext_price)}</Typography>
                      </Box>
                    </CardContent>
                  </Collapse>
                </>
              ) : (
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="subtitle2">{item.dfp_sku}</Typography>
                    <Chip label={`Qty: ${item.est_quantity ?? 0}`} size="small" variant="outlined" />
                  </Box>
                  <Typography variant="caption" color="text.secondary">{item.description}</Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mt: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">MSRP</Typography>
                      <Typography variant="body2">{fmtMoney(item.msrp)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Current</Typography>
                      <Typography variant="body2">{fmtMoney(item.current_price)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Requested</Typography>
                      <Typography variant="body2" fontWeight={600}>{fmtMoney(item.requested_price)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Competitor</Typography>
                      <Typography variant="body2">{item.competitor || '-'} {item.comp_price ? `(${fmtMoney(item.comp_price)})` : ''}</Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box>
                      <TextField
                        label="Approved Price"
                        size="small"
                        type="number"
                        disabled={!isSubmitted}
                        value={approvedVal ?? ''}
                        onChange={e => {
                          if (!item.id) return;
                          setApprovalItems(prev => ({
                            ...prev,
                            [item.id!]: { ...prev[item.id!], approved_price: e.target.value ? Number(e.target.value) : null },
                          }));
                        }}
                        sx={{ width: 160 }}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        {reqDiff != null && (
                          <Typography variant="caption" sx={{ color: reqDiff >= 0 ? 'success.main' : 'error.main' }}>
                            Req: {reqDiff >= 0 ? '+' : ''}{fmtMoney(reqDiff)}
                          </Typography>
                        )}
                        {curDiff != null && (
                          <Typography variant="caption" sx={{ color: curDiff >= 0 ? 'success.main' : 'error.main' }}>
                            Cur: {curDiff >= 0 ? '+' : ''}{fmtMoney(curDiff)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <TextField
                      label="Note"
                      size="small"
                      fullWidth
                      multiline
                      disabled={!isSubmitted}
                      value={noteVal ?? ''}
                      onChange={e => {
                        if (!item.id) return;
                        setApprovalItems(prev => ({
                          ...prev,
                          [item.id!]: { ...prev[item.id!], item_note: e.target.value },
                        }));
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.5, mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">DFP Cost: {fmtMoney(item.dfp_cost)}</Typography>
                    <Typography variant="caption" color="text.secondary">GM: {fmtPct(item.dfp_unit_gm)}</Typography>
                    <Typography variant="caption" color="text.secondary">Ext: {fmtMoney(item.ext_price)}</Typography>
                  </Box>
                </CardContent>
              )}
            </Card>
          );
        })}

        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2"><strong>Total Ext Price:</strong> {fmtMoney(totals.extPrice)}</Typography>
          <Typography variant="body2"><strong>Total Ext Cost:</strong> {fmtMoney(totals.extCost)}</Typography>
          <Typography variant="body2"><strong>GM%:</strong> {fmtPct(totals.gm)}</Typography>
        </Box>
      </Paper>

      {isSubmitted && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="contained" color="success" startIcon={<CheckCircle />}
            onClick={() => setConfirmAction('approve')}
            disabled={saving}
            sx={{ minHeight: 48, flex: isMobile ? 1 : 'none' }}
          >
            Approve
          </Button>
          <Button
            variant="outlined" color="error" startIcon={<Cancel />}
            onClick={() => setConfirmAction('reject')}
            disabled={saving}
            sx={{ minHeight: 48, flex: isMobile ? 1 : 'none' }}
          >
            Reject
          </Button>
        </Box>
      )}

      <Dialog open={confirmAction !== null} onClose={() => setConfirmAction(null)}>
        <DialogTitle>{confirmAction === 'approve' ? 'Approve Evaluation?' : 'Reject Evaluation?'}</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmAction === 'approve'
              ? 'This will approve the evaluation and notify the sales person.'
              : 'This will reject the evaluation and send it back to the sales person for revision.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            color={confirmAction === 'approve' ? 'success' : 'error'}
            onClick={() => confirmAction && handleAction(confirmAction)}
          >
            {confirmAction === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EvaluationApprovalPage;
